import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FilterService, GridDataResult, KENDO_GRID, PageChangeEvent, PagerSettings } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { Observable, Subscription } from 'rxjs';
import { SuppliersService } from './suppliers.service';
import { SupplierSearchResponseModel } from './models/supplier-search-response-model';
import { ViewItemComponent } from '../items/child-components/view-item.component';
import { ItemSearchResponseModel } from '../items/models/item-search-response-model';

@Component({
  selector: 'suppliers',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModule, KENDO_GRID],
  providers: [SuppliersService],
  templateUrl: './suppliers.component.html',
})
export class SuppliersComponent implements OnDestroy {
  public view: Observable<GridDataResult>;
  public error: boolean = false;
  public errorMessage: string = '';
  public validationMessage: string = '';
  private errorSubscription: Subscription;

  public state: State = {
    skip: 0,
    take: 10,
  };

  public pageableSettings: PagerSettings = {
    pageSizes: [10, 20, 50, 100]
  };

  public filter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [],
  };

  public itemStateOptions: { value: string; text: string }[] = [
    { value: '10', text: '10 (NEW)' },
    { value: '20', text: '20 (ACTIVE)' },
    { value: '30', text: '30 (INACTIVE)' },
    { value: '40', text: '40 (OBSOLETE)' },
    { value: '99', text: '99 (DELETED)' }
  ];

  public booleanOptions: { value: string; text: string }[] = [
    { value: 'true', text: 'YES' },
    { value: 'false', text: 'NO' }
  ];

  constructor(public suppliersService: SuppliersService, private modalService: NgbModal) {
    this.view = suppliersService;
    this.errorSubscription = this.suppliersService.errorMessage$.subscribe((message) => {
      this.errorMessage = message;
      this.error = message.length > 0;
    });
    this.suppliersService.query(this.state, this.filter);
  }

  public ngOnDestroy(): void {
    this.errorSubscription.unsubscribe();
  }

  public dismissError(): void {
    this.error = false;
    this.errorMessage = '';
  }

  public viewItem(dataItem: SupplierSearchResponseModel): void {
    const modalRef = this.modalService.open(ViewItemComponent, { size: 'size_60' });
    modalRef.componentInstance.itemModel = { itemId: dataItem.itemId } as ItemSearchResponseModel;
  }

  public getItemStateText(itemState: number | string | null | undefined): string {
    switch (Number(itemState)) {
      case 10:
        return 'NEW';
      case 20:
        return 'ACTIVE';
      case 30:
        return 'INACTIVE';
      case 40:
        return 'OBSOLETE';
      case 99:
        return 'DELETED';
      default:
        return itemState == null ? '' : String(itemState);
    }
  }

  public getFilterValue(filter: CompositeFilterDescriptor, field: string): string {
    const descriptor = this.findFieldFilter(filter, field);
    return descriptor ? String(descriptor.value ?? '') : '';
  }

  public onItemStateFilterChange(
    value: string,
    filterService: FilterService,
    filter: CompositeFilterDescriptor,
    field: string
  ): void {
    this.applyFieldFilter(field, value === '' ? null : Number(value));
  }

  public onBooleanFilterChange(
    value: string,
    filterService: FilterService,
    filter: CompositeFilterDescriptor,
    field: string
  ): void {
    this.applyFieldFilter(field, value === '' ? null : value === 'true');
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    this.state.skip = 0;
    this.suppliersService.query(this.state, this.filter);
  }

  public pageChange(state: PageChangeEvent): void {
    this.state.skip = state.skip;
    this.state.take = state.take;
    this.suppliersService.query(this.state, this.filter);
  }

  public refreshData(): void {
    this.suppliersService.query(this.state, this.filter);
  }

  private applyFieldFilter(field: string, value: number | boolean | null): void {
    const updatedFilter: CompositeFilterDescriptor = {
      logic: this.filter?.logic ?? 'and',
      filters: this.removeFieldFilter(this.filter?.filters ?? [], field)
    };

    if (value !== null) {
      updatedFilter.filters.push({ field, operator: 'eq', value });
    }

    this.filterChange(updatedFilter);
  }

  private findFieldFilter(filter: CompositeFilterDescriptor, field: string): FilterDescriptor | null {
    for (const item of filter.filters) {
      if (this.isCompositeFilter(item)) {
        const nestedFilter = this.findFieldFilter(item, field);
        if (nestedFilter) {
          return nestedFilter;
        }
      } else if (item.field === field) {
        return item;
      }
    }

    return null;
  }

  private removeFieldFilter(
    filters: Array<FilterDescriptor | CompositeFilterDescriptor>,
    field: string
  ): Array<FilterDescriptor | CompositeFilterDescriptor> {
    const cleanedFilters: Array<FilterDescriptor | CompositeFilterDescriptor> = [];

    for (const item of filters) {
      if (this.isCompositeFilter(item)) {
        const nestedFilters = this.removeFieldFilter(item.filters, field);
        if (nestedFilters.length > 0) {
          cleanedFilters.push({ logic: item.logic, filters: nestedFilters });
        }
      } else if (item.field !== field) {
        cleanedFilters.push(item);
      }
    }

    return cleanedFilters;
  }

  private isCompositeFilter(item: FilterDescriptor | CompositeFilterDescriptor): item is CompositeFilterDescriptor {
    return 'filters' in item;
  }
}
